import {
  Order,
  OrderMethod,
  OrderStatus,
  OrderType,
  PaymentStatus,
} from '@wix/ambassador-pricing-plans-v2-order/types';
import { PeriodUnit } from '@wix/ambassador-pricing-plans-v2-plan/types';
import { EndType, Period, type Plan, StartType } from '@wix/ambassador-pricing-plans-v3-plan/types';
import { CoverImage } from '@wix/pricing-plans-utils/client-data';
import type { FlowAPI } from '@wix/yoshi-flow-editor';

export const DEFAULT_MOCK_PLAN_ID = 'some-id-1';
export const HIGHLIGHTED_MOCK_PLAN_ID = 'some-id-2';
export const DEMO_FORM_ID = '050d7d21-cec6-46fb-f7bc-789fe0eff4e4';

export const demoPlanFixture = (t: FlowAPI['translations']['t']): Plan => ({
  id: 'a138c7e6-83cb-4dd3-b7cd-2c95d3719c54',
  name: 'Beginner',
  description: 'Explore the basics with this introductory course',
  currency: 'EUR',
  perks: [{ description: '6 classes' }, { description: '3 individual sessions' }, { description: 'Online resources' }],
  pricingVariants: [
    {
      id: 'e6f5b1a4-9c3d-4f2b-8a17-2d5c4e8b9f30',
      pricingStrategies: [{ flatRate: { amount: '15' } }],
      billingTerms: {
        startType: StartType.ON_PURCHASE,
        billingCycle: { count: '3', period: Period.MONTH },
        cyclesCompletedDetails: { billingCycleCount: '1' },
        endType: EndType.CYCLES_COMPLETED,
      },
      fees: [],
    },
  ],
  primary: false,
  slug: 'beginner',
  maxPurchasesPerBuyer: 0,
  buyerCanCancel: true,
  termsAndConditions: '',
  termsAndConditionsSettings: { acceptRequired: false, acceptedByDefault: false },
});

export const demoOrderFixture = (t: FlowAPI['translations']['t']): Order => ({
  id: 'b892a566-3089-48af-97d1-667a00bf7982',
  planId: 'a138c7e6-83cb-4dd3-b7cd-2c95d3719c54',
  subscriptionId: '0c7c0896-c48a-4441-9786-6d84ba632a9e',
  wixPayOrderId: '25ed8747-4953-4260-99b2-6cd4f1acc8b1',
  buyer: {
    memberId: '195e399b-e8e0-44c1-a343-932445694c1d',
    contactId: '195e399b-e8e0-44c1-a343-932445694c1d',
  },
  priceDetails: {
    subtotal: '15.00',
    discount: '0',
    total: '15.00',
    planPrice: '15',
    currency: 'EUR',
    singlePaymentForDuration: { count: 3, unit: PeriodUnit.MONTH },
  },
  pricing: {
    singlePaymentForDuration: { count: 3, unit: PeriodUnit.MONTH },
    prices: [
      {
        duration: { cycleFrom: 1, numberOfCycles: 1 },
        price: { subtotal: '15.00', discount: '0', total: '15.00', currency: 'EUR' },
      },
    ],
  },
  type: OrderType.ONLINE,
  orderMethod: OrderMethod.UNKNOWN,
  status: OrderStatus.DRAFT,
  lastPaymentStatus: PaymentStatus.UNPAID,
  pausePeriods: [],
  currentCycle: { index: 1 },
  cycles: [{ index: 1 }],
  planName: 'Beginner',
  planDescription: 'Explore the basics with this introductory course',
  planPrice: '15',
  formData: { submissionData: {} },
  statusNew: OrderStatus.DRAFT,
});

interface DemoImages {
  default: CoverImage;
  image1: CoverImage;
  image2: CoverImage;
  image3: CoverImage;
}

export const demoImages: DemoImages = {
  default: {
    uri: '11062b_1af0a135f2874bc6b584e2979480b912~mv2.jpg',
    width: 4096,
    height: 2731,
  },
  image1: {
    uri: '11062b_1cdcccfcab2e47f4bf2a74543ae52d0c~mv2.jpg',
    width: 4096,
    height: 2731,
  },
  image2: {
    uri: '11062b_e7084a8a6310484b81047d334bbf0cfd~mv2.jpeg',
    width: 4096,
    height: 2731,
  },
  image3: {
    uri: '11062b_efc2e3aa55a74f179454e88b06ec2968~mv2.jpg',
    width: 4096,
    height: 2731,
  },
};

export const plansFixtureTranslated = (t: FlowAPI['translations']['t']): Plan[] => {
  const images: DemoImages = demoImages;

  return [
    {
      id: DEFAULT_MOCK_PLAN_ID,
      name: t('demo-plans.first-plan.name'),
      description: t('demo-plans.first-plan.description'),
      currency: 'USD',
      image: { id: images.image1.uri, width: images.image1.width, height: images.image1.height },
      pricingVariants: [
        {
          id: '1a2b3c4d-0001-4000-8000-000000000001',
          pricingStrategies: [{ flatRate: { amount: '15' } }],
          billingTerms: {
            billingCycle: { count: '3', period: Period.MONTH },
            cyclesCompletedDetails: { billingCycleCount: '1' },
            endType: EndType.CYCLES_COMPLETED,
          },
          freeTrialDays: 0,
        },
      ],
      perks: [
        { description: t('demo-plans.first-perk') },
        { description: t('demo-plans.second-perk') },
        { description: t('demo-plans.third-perk') },
      ],
    },
    {
      id: HIGHLIGHTED_MOCK_PLAN_ID,
      name: t('demo-plans.second-plan.name'),
      description: t('demo-plans.second-plan.description'),
      currency: 'USD',
      image: { id: images.image2.uri, width: images.image2.width, height: images.image2.height },
      pricingVariants: [
        {
          id: '1a2b3c4d-0002-4000-8000-000000000002',
          pricingStrategies: [{ flatRate: { amount: '10' } }],
          billingTerms: {
            billingCycle: { count: '1', period: Period.MONTH },
            cyclesCompletedDetails: { billingCycleCount: '12' },
            endType: EndType.CYCLES_COMPLETED,
          },
          freeTrialDays: 0,
        },
      ],
      perks: [
        { description: t('demo-plans.first-perk') },
        { description: t('demo-plans.second-perk') },
        { description: t('demo-plans.third-perk') },
        { description: t('demo-plans.fourth-perk') },
        { description: t('demo-plans.fifth-perk') },
        { description: t('demo-plans.sixth-perk') },
      ],
    },
    {
      id: 'some-id-3',
      name: t('demo-plans.third-plan.name'),
      description: t('demo-plans.third-plan.description'),
      currency: 'USD',
      image: { id: images.image3.uri, width: images.image3.width, height: images.image3.height },
      pricingVariants: [
        {
          id: '1a2b3c4d-0003-4000-8000-000000000003',
          pricingStrategies: [{ flatRate: { amount: '30' } }],
          billingTerms: {
            billingCycle: { count: '1', period: Period.MONTH },
            endType: EndType.UNTIL_CANCELLED,
          },
          freeTrialDays: 0,
        },
      ],
      perks: [
        { description: t('demo-plans.first-perk') },
        { description: t('demo-plans.second-perk') },
        { description: t('demo-plans.third-perk') },
        { description: t('demo-plans.fourth-perk') },
        { description: t('demo-plans.fifth-perk') },
        { description: t('demo-plans.sixth-perk') },
        { description: t('demo-plans.seventh-perk') },
      ],
    },
  ];
};
